import React, { useState, useEffect, useCallback } from 'react';
import { Button } from 'antd';
import { KpiCardShell } from '~analytics-components/kpi-card-shell';
import { resolvePeriod, resolveRegion, resolveCardCompare } from './resolvers';
import { FORM_DEFINITIONS, hasNoRows } from './form-definitions';
import { SOURCES } from './sources';
import { useKpiCardData } from './use-kpi-card-data';
import { useCouponList } from './use-coupon-list';
import { CouponSelect } from './coupon-select';
import { CardEditMenu } from './card-edit-menu/card-edit-menu';
import { DegradationNote } from './degradation-cause';
import { grainInfo } from './grain-info';
import { formatComputedAt } from './format-computed-at';
import { useTweenedValue } from '~containers/analytics/components/kpi-tile/use-tweened-value';
import type { KpiCardSpec } from './card-spec';
import type {
    KpiCampaignContext,
    KpiCardData,
    KpiCardState,
    KpiSegment,
    Period,
    Region,
    ResolvedPeriod,
    ResolvedRegion,
} from './types';
import type { RegionGrain, EnvelopeCaveat } from '~stores/kpi-envelope';
import type { PageCoupon } from '../overview-page/use-page-coupon';

export interface KpiCardFromSpecProps {
    spec: KpiCardSpec;
    editMode?: boolean;
    /** Page-level filter values (Model A): used when the card pins no period/region. */
    pageRange: ResolvedPeriod;
    pageRegion: ResolvedRegion;
    /**
     * When true, the (i) popover shows only the spec's description sentence
     * (no Period / Compared / Ratio lines). Opted into by the RPP dashboard.
     */
    descriptionOnlyInfo?: boolean;
    /** How the (i) info popover opens. Defaults to 'hover': notes live in the
     * popover, so reading one should not cost a click. */
    infoTrigger?: 'hover' | 'click';
    /**
     * Page-level "Compare to previous period" state. When false, no card shows a
     * trend and the compare fetch is skipped. The Overview page now wires its own
     * toggle and passes this in; the default `true` keeps always-on trends for any
     * page that renders these cards without a compare toggle of its own.
     */
    compareEnabled?: boolean;
    /**
     * Page-level load-wave identity (filter fingerprint). Forwarded to the fetch
     * hook so a filter change restarts the load bar and drops the stale wave's
     * queued requests. Omitted by pages that don't opt into wave-drop; pacing is
     * unaffected either way.
     */
    waveKey?: string;
    /** A page-owned coupon selection (see `usePageCoupon`). When supplied, it
     * replaces this card's own coupon hook, id, and dropdown entirely, taking
     * precedence even over a spec that also sets `couponSelection`. */
    pageCoupon?: PageCoupon;
    /** The customer group this card's population is narrowed to (see
     * `KpiSegment`), owned by the block that renders the selector. Omitted by
     * every card reading a whole population. */
    segment?: KpiSegment;
    /** A campaign page's resolved window (see `KpiCampaignContext`), passed for
     * EVERY card on it, not only the `trio` ones: no card on a campaign
     * dashboard has anything to show without its campaign. Omitted elsewhere. */
    campaign?: KpiCampaignContext;
}

/**
 * Ephemeral per-card overrides, cleared on reload.
 * Region is typed here so a later slice can extend it without changing the
 * shape of the override object.
 */
interface CardOverride {
    period?: Period;
    region?: Region;
}

/**
 * Builds the info popover content from the resolved (effective) values + data.
 *
 * Order: the envelope's notes, then what the card measures (description, region
 * grain, ratio, degradation), then the period footer. Card-specific first,
 * page-wide last.
 *
 * `descriptionOnly` renders just the spec's description sentence, dropping the
 * Ratio line and the period footer. The RPP dashboard opts into this so its
 * (i) tooltips read as the plain design copy; other pages keep the full context.
 *
 * `regionGrain` (the card's source's declared grain) is appended, via
 * `grainInfo`, after the description in both modes: businessUnit/brand/national
 * cards get the honest one-line region-resolution sentence, province cards (and
 * any source with no declared grain) get nothing extra.
 *
 * An `unavailable` result is not routed here at all: with no data at all, the
 * reason is the card's visible body, not a note about it.
 */
/**
 * The response's warnings, at the TOP of the popover: they limit this figure, so
 * they are read before what it is. Only `warning` entries show; the API emits no
 * `caveat` any more, and what explains the number is the card's `description`.
 */
const caveatNotes = (
    caveats: EnvelopeCaveat[] | undefined
): React.ReactNode => {
    const warnings = (caveats ?? []).filter(
        (entry) => entry.kind === 'warning'
    );
    if (warnings.length === 0) return undefined;
    return (
        <div style={{ marginBottom: 6 }}>
            {warnings.map((entry, index) => (
                <div
                    key={`${entry.kind}-${index}`}
                    style={{
                        marginTop: index === 0 ? 0 : 4,
                        fontWeight: 600,
                        // Same amber as the (i) icon that sent the reader here:
                        // the colour is the thread from the coloured bubble to
                        // the line that made it amber.
                        color: 'var(--Warning, #d48806)',
                    }}
                >
                    {entry.text}
                </div>
            ))}
        </div>
    );
};

/**
 * The popover's footer: the window the number covers, and the window it is
 * compared against.
 *
 * Dates only. The region is deliberately absent: the reader set it themselves
 * on the page filter one row above, and a card whose region resolves
 * differently says so in its own grain sentence. The resolved DATES are the
 * part a preset ("Last 7 days") does not tell them.
 *
 * It sits at the bottom, under a rule, because it is identical on every card
 * on the page: read once, then skipped. What is specific to this card (its
 * limits, what it measures) belongs above it.
 *
 * `completeThrough` is the exception to that "identical on every card": it is
 * set only where THIS response's figures stopped short of the window above
 * them, which the Period line on its own would then misstate.
 */
const periodFooter = (
    period: ResolvedPeriod,
    compare: { range: ResolvedPeriod; label: string } | undefined,
    completeThrough: string | undefined
): React.ReactNode => (
    <div
        style={{
            marginTop: 8,
            paddingTop: 6,
            borderTop: '1px solid var(--Border-Primary, rgba(0, 0, 0, 0.1))',
            fontSize: 12,
            opacity: 0.75,
        }}
    >
        <div>
            <strong>Period:</strong> {period.start} – {period.end}
        </div>
        {completeThrough !== undefined && (
            <div>
                <strong>Figures cover:</strong> {period.start} –{' '}
                {completeThrough}
            </div>
        )}
        {compare && (
            <div>
                <strong>Compared:</strong> {compare.range.start} –{' '}
                {compare.range.end}
            </div>
        )}
    </div>
);

const buildInfo = (
    spec: KpiCardSpec,
    period: ResolvedPeriod,
    compare: { range: ResolvedPeriod; label: string } | undefined,
    data: KpiCardData | undefined,
    descriptionOnly: boolean,
    regionGrain: RegionGrain | undefined,
    caveats: EnvelopeCaveat[] | undefined,
    freshness: React.ReactNode
): React.ReactNode => {
    const regionSentence = regionGrain ? grainInfo(regionGrain) : undefined;
    const notes = caveatNotes(caveats);

    if (descriptionOnly) {
        if (!spec.description && !notes) return undefined;
        return (
            <div style={{ fontSize: 13, lineHeight: '20px', maxWidth: 240 }}>
                {notes}
                {spec.description && <div>{spec.description}</div>}
                {regionSentence && (
                    <div style={{ marginTop: 4, opacity: 0.85 }}>
                        {regionSentence}
                    </div>
                )}
                {freshness}
            </div>
        );
    }

    return (
        <div style={{ fontSize: 13, lineHeight: '20px', maxWidth: 240 }}>
            {notes}
            {spec.description && <div>{spec.description}</div>}
            {regionSentence && (
                <div style={{ marginTop: 4, opacity: 0.85 }}>
                    {regionSentence}
                </div>
            )}
            {data && data.form === 'rate' && (
                <div style={{ marginTop: 4 }}>
                    <strong>Ratio:</strong>{' '}
                    {data.numerator.toLocaleString('en-US')} of{' '}
                    {data.denominator.toLocaleString('en-US')}
                </div>
            )}
            {periodFooter(
                period,
                compare,
                data?.form === 'multiline' ? data.completeThrough : undefined
            )}
            {freshness}
        </div>
    );
};

const MUTED_LINE: React.CSSProperties = { fontSize: 12, opacity: 0.65 };

const liveStatus = (live: {
    pending: boolean;
    failed: boolean;
}): string | undefined => {
    if (live.pending) return 'Fetching live data (about 30 s)';
    if (live.failed) return 'Live data unavailable, try again later';
    return undefined;
};

/** The bubble's last lines: when the figures were computed and, on a
 * live-capable card, the "Live data" control with the state of its last click. */
const freshnessNote = (
    computedAt: string | undefined,
    live:
        | false
        | {
              pending: boolean;
              failed: boolean;
              disabled: boolean;
              onLive: () => void;
          }
): React.ReactNode => {
    if (computedAt === undefined && !live) return undefined;
    return (
        <div style={{ marginTop: 6 }}>
            {computedAt !== undefined && (
                <div style={MUTED_LINE}>{formatComputedAt(computedAt)}</div>
            )}
            {live && (
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        columnGap: 8,
                        marginTop: 4,
                    }}
                >
                    <Button
                        size="small"
                        disabled={live.disabled || live.pending}
                        onClick={live.onLive}
                    >
                        Live data
                    </Button>
                    <span aria-live="polite" style={MUTED_LINE}>
                        {liveStatus(live)}
                    </span>
                </div>
            )}
        </div>
    );
};

/**
 * Drops `trend` from card data: on a `hideTrend` card, or on stale data once
 * compare is disabled, so a re-fetch queued by turning the toggle off cannot
 * render a trend fetched before the flip.
 */
const stripTrend = (data: KpiCardData): KpiCardData => {
    switch (data.form) {
        case 'number':
        case 'rate':
            return { ...data, trend: undefined };
        case 'split':
            return {
                ...data,
                left:
                    'unavailable' in data.left
                        ? data.left
                        : { ...data.left, trend: undefined },
                right:
                    'unavailable' in data.right
                        ? data.right
                        : { ...data.right, trend: undefined },
            };
        default:
            return data;
    }
};

/**
 * Bridge between a declarative `KpiCardSpec` and the rendered card: resolves the
 * card's effective period/region (per-card override > spec pin > page filter),
 * fetches the data through the source, and renders the form picked from
 * `FORM_DEFINITIONS` inside `KpiCardShell`. Source-agnostic.
 *
 * Supports an ephemeral per-card period/region override via the `CardEditMenu`
 * in the shell's `headerAction` slot. The override is applied over the effective
 * (page-or-spec) value and is never persisted.
 */
export const KpiCardFromSpec: React.FC<KpiCardFromSpecProps> = ({
    spec,
    editMode = false,
    pageRange,
    pageRegion,
    descriptionOnlyInfo = false,
    infoTrigger = 'hover',
    compareEnabled = true,
    waveKey,
    pageCoupon,
    segment,
    campaign,
}) => {
    const [override, setOverride] = useState<CardOverride>({});

    // Model A precedence: per-card override > spec pin > page filter.
    const effectivePeriod: Period | undefined = override.period ?? spec.period;
    const effectiveRegion: Region | undefined = override.region ?? spec.region;

    const period = resolvePeriod(effectivePeriod, pageRange);
    const region = resolveRegion(effectiveRegion, pageRegion);

    // Compare basis = the concrete period in effect (the page range when following).
    const compareBasis: Period = effectivePeriod ?? pageRange;
    const compare = resolveCardCompare(
        spec.form,
        compareBasis,
        spec.trend,
        compareEnabled
    );

    // Coupon selection: a `couponSelection` card loads the coupon list and
    // auto-picks the first coupon as its default, feeding that id into the fetch
    // as `promotionId`. The hook is always called (hook rules) but only fetches
    // when enabled, so non-coupon cards are untouched. The selection state lives
    // here so slice 5's dropdown can drive it through the same setter.
    // `pageCoupon`, when supplied, replaces this per-card hook entirely (id,
    // list, and dropdown) and wins even if the spec also sets
    // `couponSelection`; the one selector lives on the page instead.
    const usingPageCoupon = pageCoupon !== undefined;
    const couponEnabled = spec.couponSelection === true || usingPageCoupon;
    const localCouponEnabled = couponEnabled && !usingPageCoupon;
    const localCouponList = useCouponList(localCouponEnabled);
    const [localSelectedCouponId, setLocalSelectedCouponId] = useState<
        string
    >();

    useEffect(() => {
        if (
            localCouponEnabled &&
            localCouponList.status === 'ready' &&
            localSelectedCouponId === undefined
        ) {
            setLocalSelectedCouponId(localCouponList.coupons[0].id);
        }
    }, [localCouponEnabled, localCouponList, localSelectedCouponId]);

    const couponList = usingPageCoupon ? pageCoupon.state : localCouponList;
    const selectedCouponId = usingPageCoupon
        ? pageCoupon.couponId
        : localSelectedCouponId;

    const promotionId = couponEnabled ? selectedCouponId : undefined;
    const selectedCoupon =
        couponList.status === 'ready'
            ? couponList.coupons.find((coupon) => coupon.id === promotionId)
            : undefined;
    const couponNotApplicable =
        spec.privateCouponsOnly === true && selectedCoupon?.isPrivate === false
            ? 'N/A: public coupon, not issued to members.'
            : spec.appTrackedCouponsOnly === true &&
              selectedCoupon?.isUnlimitedRedemption === true
            ? 'N/A: redeemed in store, not tracked in the app.'
            : undefined;
    // Hold the fetch until a coupon card has resolved its default selection, so
    // no request fires without the promotionId the endpoint needs. Hold it too
    // until a campaign page has resolved its window.
    const fetchEnabled =
        (!couponEnabled || promotionId !== undefined) &&
        couponNotApplicable === undefined &&
        (campaign === undefined || campaign.status === 'ready');

    // A click on "Live data" asks the source to compute now; the hook settles
    // it back to false once that request ends, without a second fetch.
    const [live, setLive] = useState(false);
    const settleLive = useCallback(() => setLive(false), []);

    const fetchState = useKpiCardData(
        spec.source,
        {
            form: spec.form,
            metric: spec.metric,
            period,
            region,
            compare,
            breakdownBy: spec.breakdownBy,
            breakdownBaseMetricId: spec.breakdownBaseMetricId,
            limit: spec.limit,
            palette: spec.palette,
            promotionId,
            campaignId: spec.campaignId,
            segment,
            campaignToDate:
                campaign?.status === 'ready' ? campaign.toDate : undefined,
            campaignRunsToday:
                campaign?.status === 'ready' ? campaign.runsToday : undefined,
            sample: spec.sample,
            live,
        },
        waveKey,
        fetchEnabled,
        settleLive
    );

    // Surface the coupon-list lifecycle on the card: a failed list is an error
    // tile, an empty list is a clear "no coupons" message, and while it loads (or
    // before the default resolves) the fetch is gated, so `fetchState` is loading.
    let state: KpiCardState = fetchState;
    if (couponEnabled) {
        if (couponList.status === 'error') {
            state = { status: 'error' };
        } else if (couponList.status === 'empty') {
            state = {
                status: 'unavailable',
                reason: 'No coupons available.',
            };
        } else if (couponNotApplicable !== undefined) {
            state = { status: 'unavailable', reason: couponNotApplicable };
        }
    }
    if (campaign?.status === 'unavailable') {
        state = { status: 'unavailable', reason: campaign.reason };
    }

    const isModified =
        override.period !== undefined || override.region !== undefined;
    const isSample = SOURCES[spec.source].sample === true;

    const Renderer = FORM_DEFINITIONS[spec.form].render;

    // Dev-time guard: a source only produces the forms in its descriptor. Pairing
    // a spec with an unsupported form makes the source return a different shape,
    // which the renderer's form check silently blanks. Warn instead of failing
    // quietly. Stripped from production builds.
    useEffect(() => {
        if (import.meta.env.MODE === 'production') return;
        const supported = SOURCES[spec.source].descriptor.forms;
        if (!supported.includes(spec.form)) {
            const forms = supported.join(', ');
            console.warn(
                `KpiCard "${spec.title}": source "${spec.source}" does not support form "${spec.form}" (supports: ${forms}). The card will render blank.`
            );
        }
    }, [spec.source, spec.form, spec.title]);

    // Dev-time guard: with `descriptionOnlyInfo`, the (i) popover renders only the
    // spec's description, so a spec with no description shows no (i) icon at all.
    // Warn so a description-less card does not silently lose its info affordance.
    // Stripped from production builds.
    useEffect(() => {
        if (import.meta.env.MODE === 'production') return;
        if (descriptionOnlyInfo && !spec.description) {
            console.warn(
                `KpiCard "${spec.title}": descriptionOnlyInfo is set but the spec has no description, so the card renders no (i) info affordance.`
            );
        }
    }, [descriptionOnlyInfo, spec.description, spec.title]);

    // The edit menu shows the resolved values as its starting point, so a card
    // following the page filter opens pre-filled with the current page values.
    const editMenu = (
        <CardEditMenu
            effectivePeriod={compareBasis}
            onChange={(newPeriod) =>
                setOverride((prev) => ({ ...prev, period: newPeriod }))
            }
            effectiveRegion={region}
            onRegionChange={(newRegion) =>
                setOverride((prev) => ({ ...prev, region: newRegion }))
            }
            onReset={() => setOverride({})}
        />
    );

    // Rendered once the local coupon list is ready. A page-supplied selection
    // skips this: the page's one shared selector (see `usePageCoupon`) covers
    // it instead of duplicating one per card.
    const couponControl =
        !usingPageCoupon && couponEnabled && couponList.status === 'ready' ? (
            <CouponSelect
                coupons={couponList.coupons}
                value={localSelectedCouponId}
                onChange={setLocalSelectedCouponId}
            />
        ) : undefined;

    // The content kept through a re-fetch, or beside the error of a failed
    // live request.
    const staleCard =
        state.status === 'loading' || state.status === 'error'
            ? state.stale
            : undefined;
    // `ready` data, or the previous `ready` data carried through a re-fetch
    // (`stale`) so the card keeps its content instead of blanking.
    const fetchedData =
        state.status === 'ready'
            ? state.data
            : staleCard
            ? compareEnabled
                ? staleCard.data
                : stripTrend(staleCard.data)
            : undefined;
    const readyData =
        fetchedData && spec.hideTrend ? stripTrend(fetchedData) : fetchedData;
    const computedAt =
        state.status === 'ready' ? state.computedAt : staleCard?.computedAt;
    // `unavailable` and a bare `error` have no envelope to carry notes from.
    const envelopeCaveats =
        state.status === 'ready' ? state.caveats : staleCard?.caveats;
    // Yellow only on a warning: a caveat is true of every response, so
    // colouring on it would light every card.
    const hasWarning = (envelopeCaveats ?? []).some(
        (entry) => entry.kind === 'warning'
    );
    const liveCapable =
        !couponEnabled && SOURCES[spec.source].descriptor.liveCapable === true;
    const liveFailed = state.status === 'error' && state.stale !== undefined;
    const freshness = freshnessNote(
        computedAt,
        liveCapable && {
            pending: live,
            failed: liveFailed,
            disabled: !fetchEnabled,
            onLive: () => setLive(true),
        }
    );
    const answered = staleCard ?? { period, compare };
    const served = state.status === 'ready' ? state.served : staleCard?.served;
    const info = buildInfo(
        spec,
        served?.range ?? answered.period,
        spec.hideTrend || !answered.compare
            ? undefined
            : {
                  ...answered.compare,
                  range: served?.compareRange ?? answered.compare.range,
              },
        readyData,
        descriptionOnlyInfo,
        SOURCES[spec.source].descriptor.regionGrain,
        envelopeCaveats,
        freshness
    );

    // With `barBaseInHeader`, a bars card carries its shared base in the title
    // (e.g. "Badges (15,787)") instead of on every row. A source declaring a
    // shared denominator reports it on the card data, so the title still shows
    // it when every step is unavailable; otherwise fall back to the first rate
    // row's own base, which is how per-row bases reach the header.
    let barBase: number | undefined;
    if (spec.barBaseInHeader && readyData?.form === 'bars') {
        barBase = readyData.base;
        if (barBase == null) {
            for (const step of readyData.steps) {
                if (
                    !('unavailable' in step) &&
                    step.rate != null &&
                    step.base != null
                ) {
                    barBase = step.base;
                    break;
                }
            }
        }
    }
    const tweenedBarBase = useTweenedValue(barBase ?? NaN);
    const cardTitle =
        barBase != null
            ? `${spec.title} (${Math.round(tweenedBarBase).toLocaleString(
                  'en-US'
              )})`
            : spec.title;

    return (
        <KpiCardShell
            title={cardTitle}
            subtitle={spec.subtitle}
            size={spec.size}
            info={info}
            infoTrigger={infoTrigger}
            infoTone={hasWarning ? 'warning' : undefined}
            infoInteractive={liveCapable}
            isLoading={state.status === 'loading' && state.stale === undefined}
            loadingNote={period.preset ? undefined : 'Computing this period…'}
            headerControl={couponControl}
            headerAction={editMode ? editMenu : undefined}
            modified={!editMode && isModified}
            sample={isSample}
        >
            {/* A form that has to show a reader something is missing carries it
                in its own DATA (see the `downloads` form's `missing`), because
                only the source can tell a vanished dataset from a caveat. */}
            {readyData !== undefined && hasNoRows(readyData) && (
                <span className="footnote_description">
                    No data for this period
                </span>
            )}
            {readyData !== undefined && !hasNoRows(readyData) && (
                <Renderer
                    data={readyData}
                    size={spec.size}
                    invertTrend={spec.invertTrend}
                    barBaseInHeader={spec.barBaseInHeader}
                    toDateOnly={spec.toDateOnly}
                    headlineFromFirstSlice={spec.headlineFromFirstSlice}
                    legendBelow={spec.legendBelow}
                    legendBeside={spec.legendBeside}
                    palette={spec.palette}
                    barsStyle={spec.barsStyle}
                    shareAxis={spec.shareAxis}
                    chartStyle={spec.chartStyle}
                    subLabelUnit={spec.subLabelUnit}
                    boxed={spec.boxed}
                />
            )}
            {state.status === 'pending' && (
                <span className="footnote_description">Awaiting data</span>
            )}
            {state.status === 'error' && !liveFailed && (
                <span className="footnote_description">Data unavailable</span>
            )}
            {state.status === 'unavailable' && (
                <DegradationNote cause={state.cause} reason={state.reason} />
            )}
        </KpiCardShell>
    );
};
